import { Component, ElementRef, Inject, OnDestroy, OnInit, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslatePipe } from '@shared/pipes/translate.pipe';

export interface DrawioEditorDialogData {
  name: string;
  /** XML del mxfile; null para un diagrama nuevo en blanco. */
  xml: string | null;
}

export interface DrawioEditorResult {
  xml: string;
  /** Nombre (editable en la cabecera del editor). */
  name: string;
  /** PNG exportado en base64 (sin prefijo data:). */
  previewPngBase64: string | null;
}

const EMBED_ORIGIN = 'https://embed.diagrams.net';
const EMBED_URL = `${EMBED_ORIGIN}/?embed=1&proto=json&spin=1&ui=min&libraries=1&saveAndExit=1`;

/**
 * Editor draw.io en modo embebido nativo (protocolo JSON por postMessage).
 * "Guardar y salir" en el editor guarda: pedimos también el export PNG
 * para la miniatura y devolvemos {xml, previewPngBase64}.
 */
@Component({
  selector: 'app-drawio-editor-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, TranslatePipe],
  template: `
    <div class="drawio-wrap">
      <div class="drawio-header">
        <mat-icon class="drawio-logo">account_tree</mat-icon>
        <input class="drawio-name-input"
               type="text"
               maxlength="255"
               [value]="name()"
               (input)="name.set($any($event.target).value)"
               [attr.aria-label]="data.name">
        @if (saving()) {
          <mat-spinner diameter="18"></mat-spinner>
          <span class="saving-hint">{{ 'gdp.diagrams.saving' | translate }}</span>
        }
        <span class="spacer"></span>
        <button mat-icon-button (click)="close()"><mat-icon>close</mat-icon></button>
      </div>
      <iframe #frame class="drawio-frame" [src]="frameUrl" title="draw.io"></iframe>
    </div>
  `,
  styles: [`
    .drawio-wrap {
      display: flex;
      flex-direction: column;
      width: 94vw;
      height: 92vh;
    }
    .drawio-header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 8px 8px 16px;
      border-bottom: 1px solid var(--gdp-border, #e6e8f0);
    }
    .drawio-logo { color: var(--brand-cyan, #29abe2); }
    .drawio-name-input {
      font-size: 0.9rem;
      font-weight: 700;
      color: var(--gdp-ink, #1b1464);
      font-family: inherit;
      border: 1px solid transparent;
      border-radius: 6px;
      padding: 4px 8px;
      min-width: 260px;
      background: transparent;
    }
    .drawio-name-input:hover { border-color: var(--gdp-border, #e6e8f0); }
    .drawio-name-input:focus {
      outline: none;
      border-color: var(--brand-cyan, #29abe2);
      background: white;
    }
    .saving-hint { font-size: 0.78rem; color: var(--gdp-muted, #6b7280); }
    .spacer { flex: 1 1 auto; }
    .drawio-frame {
      flex: 1 1 auto;
      border: 0;
      width: 100%;
    }
  `]
})
export class DrawioEditorDialogComponent implements OnInit, OnDestroy {
  readonly frameUrl: SafeResourceUrl;
  readonly saving = signal(false);
  /** Nombre editable del diagrama. */
  readonly name = signal('');

  private readonly frameRef = viewChild.required<ElementRef<HTMLIFrameElement>>('frame');

  private savedXml: string | null = null;
  private readonly onMessage = (event: MessageEvent) => this.handleMessage(event);

  private readonly sanitizer = inject(DomSanitizer);

  constructor(
    public dialogRef: MatDialogRef<DrawioEditorDialogComponent, DrawioEditorResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: DrawioEditorDialogData
  ) {
    this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(EMBED_URL);
    this.name.set(data.name);
  }

  ngOnInit(): void {
    window.addEventListener('message', this.onMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
  }

  close(): void {
    this.dialogRef.close(undefined);
  }

  private handleMessage(event: MessageEvent): void {
    if (event.origin !== EMBED_ORIGIN || typeof event.data !== 'string' || !event.data.startsWith('{')) {
      return;
    }
    let message: { event?: string; xml?: string; data?: string };
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    switch (message.event) {
      case 'init':
        this.post({ action: 'load', xml: this.data.xml ?? '', autosave: 0 });
        break;
      case 'save':
        // Guardar: nos quedamos el XML y pedimos la preview PNG
        this.savedXml = message.xml ?? null;
        this.saving.set(true);
        this.post({ action: 'export', format: 'png', spinKey: 'saving' });
        break;
      case 'export': {
        const xml = this.savedXml;
        if (!xml) {
          return;
        }
        const base64 = message.data?.split(',')[1] ?? null;
        this.dialogRef.close({ xml, name: this.name().trim() || this.data.name, previewPngBase64: base64 });
        break;
      }
      case 'exit':
        this.dialogRef.close(undefined);
        break;
    }
  }

  private post(message: object): void {
    this.frameRef().nativeElement.contentWindow?.postMessage(JSON.stringify(message), EMBED_ORIGIN);
  }
}
